import React, { useCallback, useEffect, useState } from 'react';
import {
  Typography,
  Box,
  Paper,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { 
  Add, 
  Edit, 
  Delete, 
  CloudUpload, 
  ArrowBack,
  Build,
  Description,
  NoteAdd
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { structuresApi } from '../../services/benchmarkApi';

const RootDiv = styled('div')({
  flexGrow: 1,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
});

const ContentDiv = styled('div')({
  flexGrow: 1,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  padding: '0',
  paddingTop: '8px',
});

const StyledPaper = styled(Paper)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 2,
  display: 'flex',
  flexDirection: 'column',
  height: 'calc(100vh - 120px)',
  overflow: 'hidden',
  backgroundColor: theme.palette.background.paper,
  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
  margin: theme.spacing(1),
}));

const HeaderContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
}));

const TitleBar = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2, 2),
  backgroundColor: 'transparent',
  color: theme.palette.text.primary,
}));

const ButtonGroup = styled('div')({
  display: 'flex',
  gap: 8,
  alignItems: 'center',
  '& > *': {
    display: 'flex',
    alignItems: 'center',
  },
});

const Title = styled(Typography)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  fontWeight: 600,
  '& svg': {
    marginRight: theme.spacing(1),
    color: theme.palette.primary.main,
  },
}));

const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  border: 'none',
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: '#F8F8F8',
    color: '#6B0F2A',
    fontWeight: 600,
    fontSize: '0.875rem',
  },
  '& .MuiDataGrid-cell': {
    borderBottom: `1px solid ${theme.palette.divider}`,
    fontSize: '0.8125rem',
  },
  '& .MuiDataGrid-row:hover': {
    backgroundColor: 'rgba(139, 159, 232, 0.08)',
  },
  '& .MuiDataGrid-row.Mui-selected': {
    backgroundColor: 'rgba(139, 159, 232, 0.16)',
    '&:hover': {
      backgroundColor: 'rgba(139, 159, 232, 0.24)',
    },
  },
  '& .MuiDataGrid-footerContainer': {
    borderTop: `1px solid ${theme.palette.divider}`,
    borderBottomLeftRadius: theme.shape.borderRadius * 2,
    borderBottomRightRadius: theme.shape.borderRadius * 2,
  },
  '& .MuiTablePagination-root': {
    color: theme.palette.text.secondary,
  },
}));

const mapNodeRow = (n) => ({
  id: n.id || n.structureNodeId,
  budgetCode: n.structureCode || '',
  description: n.structureName || n.description || '',
  costType: n.structureTypeCode || '',
  structureTypeId: n.structureTypeId,
});

const BenchmarkCodeStandard = () => {
  const navigate = useNavigate();

  const [selectedRow, setSelectedRow] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [uploadExcelDialogOpen, setUploadExcelDialogOpen] = useState(false);
  const [addHeaderDialogOpen, setAddHeaderDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [documentFormatName, setDocumentFormatName] = useState('');
  const [documentFormatNames, setDocumentFormatNames] = useState([]);
  const [selectedDocumentFormat, setSelectedDocumentFormat] = useState('');
  const [formData, setFormData] = useState({
    budgetCode: '',
    description: '',
    costType: '',
  });

  const loadTypes = useCallback(async () => {
    try {
      const res = await structuresApi.listTypes();
      const types = (res.data || []).map((t) => ({
        id: t.structureTypeId,
        name: t.structureName || t.structureCode,
        code: t.structureCode,
      }));
      setDocumentFormatNames(types);
      setSelectedDocumentFormat((prev) => {
        if (prev) return prev;
        return types.length ? String(types[0].id) : '';
      });
    } catch (e) {
      setError(e.message || 'Failed to load structure types');
      setDocumentFormatNames([]);
    }
  }, []);

  const loadNodes = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (selectedDocumentFormat) {
        params.structureTypeId = Number(selectedDocumentFormat);
      }
      const res = await structuresApi.listNodes(params);
      let nodes = res.data || [];
      if (selectedDocumentFormat) {
        const typeId = Number(selectedDocumentFormat);
        nodes = nodes.filter((n) => n.structureTypeId === typeId);
      }
      setRows(nodes.map(mapNodeRow));
    } catch (e) {
      setError(e.message || 'Failed to load structure nodes');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [selectedDocumentFormat]);

  useEffect(() => {
    loadTypes();
  }, [loadTypes]);

  useEffect(() => {
    loadNodes();
  }, [loadNodes]);

  const handleRowClick = (params) => {
    setSelectedRow(params.row);
  };

  const handleAdd = () => {
    setEditMode(false);
    setFormData({
      budgetCode: '',
      description: '',
      costType: '',
    });
    setDialogOpen(true);
  };

  const handleEdit = () => {
    if (!selectedRow) {
      return;
    }
    setEditMode(true);
    setFormData({ ...selectedRow });
    setDialogOpen(true);
  };

  const handleDelete = () => {
    if (!selectedRow) {
      return;
    }
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedRow) {
      setDeleteDialogOpen(false);
      return;
    }
    setError('');
    try {
      await structuresApi.deleteNode(selectedRow.id);
      setSelectedRow(null);
      setDeleteDialogOpen(false);
      await loadNodes();
    } catch (e) {
      setError(e.message || 'Failed to delete structure node');
      setDeleteDialogOpen(false);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditMode(false);
    setFormData({
      budgetCode: '',
      description: '',
      costType: '',
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    setError('');
    if (!selectedDocumentFormat && !editMode) {
      setError('Select a Document Format (structure type) before adding a code');
      return;
    }
    const payload = {
      structureCode: formData.budgetCode,
      structureName: formData.description,
      description: formData.description,
      structureTypeId: selectedDocumentFormat
        ? Number(selectedDocumentFormat)
        : selectedRow?.structureTypeId,
    };
    try {
      if (editMode && selectedRow) {
        await structuresApi.updateNode(selectedRow.id, payload);
      } else {
        await structuresApi.createNode(payload);
      }
      handleCloseDialog();
      setSelectedRow(null);
      await loadNodes();
    } catch (e) {
      setError(e.message || 'Failed to save structure node');
    }
  };

  const handleUploadExcel = () => {
    setUploadExcelDialogOpen(true);
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      const extension = file.name.split('.').pop().toLowerCase();
      if (['xlsx', 'xls'].includes(extension)) {
        setSelectedFile(file);
      } else {
        alert('Please select a valid Excel file (.xlsx or .xls)');
      }
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragOver(false);
    const file = event.dataTransfer.files[0];
    if (file) {
      const extension = file.name.split('.').pop().toLowerCase();
      if (['xlsx', 'xls'].includes(extension)) {
        setSelectedFile(file);
      } else {
        alert('Please select a valid Excel file (.xlsx or .xls)');
      }
    }
  };

  const handleUpload = () => {
    if (!selectedFile) {
      alert('Please select an Excel file');
      return;
    }
    // TODO: Implement actual upload logic
    console.log('Uploading file:', selectedFile);
    alert('File upload functionality will be implemented');
    setUploadExcelDialogOpen(false);
    setSelectedFile(null);
  };

  const handleCloseUploadDialog = () => {
    setUploadExcelDialogOpen(false);
    setSelectedFile(null);
    setIsDragOver(false);
  };

  const handleAddHeader = () => {
    setAddHeaderDialogOpen(true);
  };

  const handleCloseAddHeaderDialog = () => {
    setAddHeaderDialogOpen(false);
    setDocumentFormatName('');
  };

  const handleAddDocumentFormat = () => {
    if (!documentFormatName.trim()) {
      alert('Please enter a Document Format Name');
      return;
    }
    const newId = Math.max(...documentFormatNames.map((d) => d.id), 0) + 1;
    setDocumentFormatNames((prev) => [...prev, { id: newId, name: documentFormatName }]);
    setDocumentFormatName('');
  };

  const handleDeleteDocumentFormat = (id) => {
    setDocumentFormatNames((prev) => prev.filter((d) => d.id !== id));
    if (selectedDocumentFormat === id.toString()) {
      setSelectedDocumentFormat('');
    }
  };

  const columns = [
    { 
      field: 'budgetCode', 
      headerName: 'Budget Code', 
      width: 200,
      minWidth: 150,
    },
    { 
      field: 'description', 
      headerName: 'Description', 
      width: 400,
      minWidth: 300,
      flex: 1,
    },
    { 
      field: 'costType', 
      headerName: 'Structure Type', 
      width: 150,
    },
  ];

  return (
    <RootDiv>
      <ContentDiv>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1, px: 1 }}>
          <FormControl 
            size="small" 
            sx={{ 
              minWidth: 200, 
              '& .MuiOutlinedInput-root': { height: '40px' },
            }}
          >
            <InputLabel>Document Format Name</InputLabel>
            <Select
              value={selectedDocumentFormat}
              onChange={(e) => setSelectedDocumentFormat(e.target.value)}
              label="Document Format Name"
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 300,
                  },
                },
              }}
            >
              <MenuItem value="">All</MenuItem>
              {documentFormatNames.map((format) => (
                <MenuItem key={format.id} value={format.id.toString()}>
                  {format.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <StyledPaper>
          <HeaderContainer>
            <TitleBar>
              <Title variant="h6">
                <Build fontSize="small" />
                Cost Structure
              </Title>
              <ButtonGroup>
                <Tooltip title="Add Header">
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<NoteAdd />}
                    onClick={handleAddHeader}
                    sx={{ height: '40px' }}
                  >
                    Add Header
                  </Button>
                </Tooltip>
                <Tooltip title="Add New Cost Structure">
                  <IconButton 
                    color="primary" 
                    onClick={handleAdd} 
                    size="small"
                  >
                    <Add />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Edit Selected Cost Structure">
                  <IconButton 
                    color="primary" 
                    onClick={handleEdit} 
                    disabled={!selectedRow} 
                    size="small"
                  >
                    <Edit />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete Selected Cost Structure">
                  <IconButton 
                    color="error" 
                    onClick={handleDelete} 
                    disabled={!selectedRow} 
                    size="small"
                  >
                    <Delete />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Upload Excel">
                  <IconButton 
                    color="primary" 
                    onClick={handleUploadExcel} 
                    size="small"
                  >
                    <CloudUpload />
                  </IconButton>
                </Tooltip>
              </ButtonGroup>
            </TitleBar>
            {error && (
              <Alert severity="error" sx={{ mx: 2, mb: 1 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}
          </HeaderContainer>
          
          <Box
            sx={{
              flexGrow: 1,
              width: '100%',
              height: '100%',
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            {loading && (
              <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', zIndex: 1 }}>
                <CircularProgress size={28} />
              </Box>
            )}
            <StyledDataGrid
              rows={rows}
              columns={columns}
              pageSizeOptions={[10, 25, 50, 100]}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 25 },
                },
              }}
              onRowClick={handleRowClick}
              disableRowSelectionOnClick
              density="compact"
              autoHeight={false}
              headerHeight={48}
            />
          </Box>
        </StyledPaper>
      </ContentDiv>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: 'primary.main', color: 'background.paper' }}>
          {editMode ? 'Edit Cost Structure' : 'Add New Cost Structure'}
        </DialogTitle>
        <DialogContent dividers sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Budget Code"
                name="budgetCode"
                value={formData.budgetCode}
                onChange={handleInputChange}
                required
                size="small"
                margin="dense"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                required
                size="small"
                margin="dense"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small" margin="dense">
                <InputLabel>Cost Type</InputLabel>
                <Select
                  name="costType"
                  value={formData.costType}
                  onChange={handleInputChange}
                  label="Cost Type"
                >
                  <MenuItem value="Fixed">Fixed</MenuItem>
                  <MenuItem value="Variable">Variable</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary" variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleSave} color="primary" variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this code standard? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="primary" variant="outlined">
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Excel Upload Dialog */}
      <Dialog open={uploadExcelDialogOpen} onClose={handleCloseUploadDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: 'primary.main', color: 'background.paper' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CloudUpload sx={{ mr: 1 }} />
            Upload Excel File
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ mt: 2 }}>
          <Box
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => document.getElementById('excel-upload-input-code-standard').click()}
            sx={{
              border: '2px dashed',
              borderColor: isDragOver ? 'primary.main' : 'grey.300',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              backgroundColor: isDragOver ? 'primary.light' : 'grey.50',
              transition: 'all 0.3s ease',
              mb: 3,
              cursor: 'pointer',
            }}
          >
            <CloudUpload
              sx={{
                fontSize: 48,
                color: isDragOver ? 'primary.main' : 'grey.400',
                mb: 2
              }}
            />
            <Typography variant="h6" gutterBottom>
              {isDragOver ? 'Drop Excel file here' : 'Drag & Drop Excel file here'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              or click to select a file
            </Typography>

            <input
              accept=".xlsx,.xls"
              style={{ display: 'none' }}
              id="excel-upload-input-code-standard"
              type="file"
              onChange={handleFileSelect}
            />
            <label htmlFor="excel-upload-input-code-standard">
              <Button
                variant="contained"
                component="span"
                startIcon={<CloudUpload />}
                onClick={(e) => e.stopPropagation()}
              >
                Select Excel File
              </Button>
            </label>

            <Typography variant="caption" display="block" sx={{ mt: 2, color: 'text.secondary' }}>
              Supported formats: .xlsx, .xls
            </Typography>
          </Box>

          {selectedFile && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Selected File:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <Description />
                  </ListItemIcon>
                  <ListItemText
                    primary={selectedFile.name}
                    secondary={`${(selectedFile.size / 1024).toFixed(2)} KB`}
                  />
                </ListItem>
              </List>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUploadDialog} color="primary" variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            color="primary"
            variant="contained"
            disabled={!selectedFile}
            startIcon={<CloudUpload />}
          >
            Upload
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Header Dialog */}
      <Dialog open={addHeaderDialogOpen} onClose={handleCloseAddHeaderDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ backgroundColor: 'primary.main', color: 'background.paper' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <NoteAdd sx={{ mr: 1 }} />
            Add Document Format Name
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Document Format Name"
                value={documentFormatName}
                onChange={(e) => setDocumentFormatName(e.target.value)}
                size="small"
                margin="dense"
                placeholder="Enter document format name"
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleAddDocumentFormat}
                disabled={!documentFormatName.trim()}
                size="small"
              >
                Add Format
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2, mb: 1 }}>
                Document Format Names:
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Document Format Name</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: 100 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {documentFormatNames.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={2} align="center">
                          <Typography variant="body2" color="text.secondary">
                            No document format names added yet
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      documentFormatNames.map((format) => (
                        <TableRow key={format.id}>
                          <TableCell>{format.name}</TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteDocumentFormat(format.id)}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddHeaderDialog} color="primary" variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </RootDiv>
  );
};

export default BenchmarkCodeStandard;
